import { useCallback, useMemo, useRef, useState } from "react";
import ToolAccessDialog from "./ToolAccessDialog";
import ToolGroup from "./ToolGroup";
import type { MCPConnectionTool } from "../../../services/agent-tools-service";
import { useTranslation } from "react-i18next";
import { useMCPQueryScope } from "../hooks/useMCPQueryScope";
import { mcpQueryKeys } from "../query-keys";

type Props = {
  connectionId: string;
  tools: MCPConnectionTool[];
  isLoading?: boolean;
  errorMessage?: string | null;
  canManage: boolean;
};

const PermissionsSession = ({
  connectionId,
  tools,
  isLoading = false,
  errorMessage = null,
  canManage,
}: Props) => {
  const { t } = useTranslation();
  const [accessTool, setAccessTool] = useState<MCPConnectionTool | null>(null);
  const sectionRef = useRef<HTMLElement>(null);
  const getFocusFallback = useCallback((toolId: string): HTMLElement[] => {
    const section = sectionRef.current;
    if (!section?.isConnected) return [];
    // This owner is remounted for each connection/scope. Never search another
    // panel for a same-ID tool or keep a ref to an obsolete replacement row.
    const replacement = Array.from(section.querySelectorAll<HTMLButtonElement>("button[data-mcp-tool-access]"))
      .find((button) => button.dataset.mcpToolAccess === toolId);
    return replacement ? [replacement, section] : [section];
  }, []);

  // Servers report a tool's type freely, so bucket the ones we know how to
  // label and keep the rest — "other", and anything unrecognized or missing —
  // in their own group rather than implying they write or delete.
  const { readTools, writeTools, otherTools } = useMemo(() => {
    const read: MCPConnectionTool[] = [];
    const write: MCPConnectionTool[] = [];
    const other: MCPConnectionTool[] = [];
    for (const tool of tools) {
      if (tool.type === "read") read.push(tool);
      else if (tool.type === "write" || tool.type === "delete") write.push(tool);
      else other.push(tool);
    }
    return { readTools: read, writeTools: write, otherTools: other };
  }, [tools]);

  // Only authoritative removal destroys the draft. Transient loading/errors
  // block Save in the dialog without losing same-session edits. A removed tool
  // returning later still requires another explicit Access click.
  const missingTool = accessTool !== null && !isLoading && !errorMessage && !tools.some((tool) => tool.id === accessTool.id);
  if (missingTool) {
    setAccessTool(null);
  }

  return (
    <section ref={sectionRef} tabIndex={-1} aria-label={t("mcpPage.toolPermissions.title")} className="px-4 py-2">
      <h3 className="flex h-[45px] items-center py-3 text-base font-medium text-foreground">
        {t("mcpPage.toolPermissions.title")}
      </h3>

      {isLoading && tools.length === 0 ? (
        <p className="py-3 text-sm text-muted-foreground">
          {t("mcpPage.toolPermissions.loading")}
        </p>
      ) : errorMessage ? (
        <p className="py-3 text-sm text-destructive">{errorMessage}</p>
      ) : tools.length === 0 ? (
        <p className="py-3 text-sm text-muted-foreground">
          {t("mcpPage.toolPermissions.empty")}
        </p>
      ) : (
        <>
          {readTools.length > 0 && (
            <ToolGroup
              title={t("mcpPage.toolPermissions.readOnly")}
              tools={readTools}
              onToolChangeAccess={setAccessTool}
              canManage={canManage}
              defaultOpen
            />
          )}
          {writeTools.length > 0 && (
            <ToolGroup
              title={t("mcpPage.toolPermissions.writeDelete")}
              tools={writeTools}
              onToolChangeAccess={setAccessTool}
              canManage={canManage}
            />
          )}
          {otherTools.length > 0 && (
            <ToolGroup
              title={t("mcpPage.toolPermissions.other")}
              tools={otherTools}
              onToolChangeAccess={setAccessTool}
              canManage={canManage}
            />
          )}
        </>
      )}

      <ToolAccessDialog
        open={accessTool !== null && !missingTool}
        onOpenChange={(open) => {
          if (!open) setAccessTool(null);
        }}
        connectionId={connectionId}
        tool={accessTool}
        canManage={canManage}
        getFocusFallback={getFocusFallback}
      />
    </section>
  );
};

// Selection belongs to a connection in one canonical request scope, not to a
// reusable detail-panel instance. Returning A → B → A creates a fresh owner.
const PermissionsSection = (props: Props) => {
  const scope = useMCPQueryScope();
  return <PermissionsSession key={JSON.stringify([mcpQueryKeys.scope(scope.key), props.connectionId])} {...props} />;
};

export default PermissionsSection;
